import React, { useEffect } from "react";
import { useGlobalNotifier } from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfFooter,
  FrVrfMultipleLineInputField,
  FrVrfIcon,
  FrVrfTextDisplayField,
} from "@vrf/fr-vrf-web-components-react";
import { createModal } from "../../modal";
import {
  ConfirmationWithCommentDialogInitialState,
  useConfirmationWithCommentDialogState,
} from ".";
import { usePageNavigation } from "../../../pages/use-page-navigation";

const VRF_PRIMARY_COLOR = "#38468c";

export const ConfirmationWithCommentDialogContent: React.FC = () => {
  const { resetUserError } = useGlobalNotifier();
  const [pageState, setPageState] = useConfirmationWithCommentDialogState();
  const { moveToModalPage, closeModalPage } = usePageNavigation();

  const closeModal = () => {
    closeModalPage();
    resetUserError();
    setPageState({
      ...ConfirmationWithCommentDialogInitialState,
      subTitle: undefined,
      onNgButtonClicked: undefined,
      onOkButtonClicked: undefined,
    });
  };

  const onInitialDisplayed = (): void => {
    moveToModalPage();
    resetUserError();
  };
  useEffect(onInitialDisplayed, []);

  const onChangeValueHandle = (event: CustomEvent) => {
    const value = event.detail.value;
    const isValid = event.detail.isValid;

    setPageState((prevState) => ({
      ...prevState,
      comment: value,
      isValid: isValid,
    }));
  };

  const onClickCancel = () => {
    resetUserError();
    closeModal();
  };

  const onClickNg = () => {
    resetUserError();
    if (pageState.onNgButtonClicked)
      pageState.onNgButtonClicked(false, pageState.comment);
    closeModal();
  };

  const onClickOk = () => {
    resetUserError();
    if (pageState.onOkButtonClicked)
      pageState.onOkButtonClicked(true, pageState.comment);
    closeModal();
  };

  const getContent = () => {
    return (
      <>
        <div style={{ display: "flex" }}>
          <FrVrfIcon
            icon={icon[pageState.type].icon}
            style={{
              "--vrf-icon-size": "38px",
              margin: "4px",
            }}
          />
          <FrVrfTextDisplayField
            style={{
              "--vrf-font-color": VRF_PRIMARY_COLOR,
              "--vrf-text-display-field-font-size": "25px",
              "--vrf-text-display-field-font-weight": "bold",
            }}
            text={pageState.subTitle || icon[pageState.type].title}
          />
        </div>
        <FrVrfTextDisplayField text={pageState.message} />
        <FrVrfMultipleLineInputField
          labelContent="Comment"
          content={pageState.comment}
          resize="both"
          style={{
            marginTop: "30px",
            "--vrf-multiple-line-input-field-input-height": "100px",
            "--vrf-multiple-line-input-field-input-width": "600px",
          }}
          constraint="[Size(value, {max: 2000})]"
          onInputValueChanged={onChangeValueHandle}
        />
        <FrVrfFooter>
          {pageState.layout.showCancelButton ? (
            <FrVrfButton
              size="large"
              content={pageState.layout.cancelButtonTitle}
              theme="secondary"
              onClicked={onClickCancel}
            />
          ) : undefined}
          {pageState.layout.showNgButton ? (
            <FrVrfButton
              size="large"
              content={pageState.layout.ngButtonTitle}
              theme="primary"
              onClicked={onClickNg}
              disabled={
                (pageState.layout.reqCommClickNg && pageState.comment === "") ||
                !pageState.isValid
              }
            />
          ) : undefined}
          <FrVrfButton
            size="large"
            theme={
              pageState.layout.okButtonTitle === "DELETE"
                ? "caution"
                : "primary"
            }
            content={pageState.layout.okButtonTitle}
            onClicked={onClickOk}
            disabled={
              (pageState.layout.reqCommClickOk && pageState.comment === "") ||
              !pageState.isValid
            }
          />
        </FrVrfFooter>
      </>
    );
  };

  return createModal(
    pageState.title,
    getContent(),
    () => closeModal(),
    "medium",
  );
};

const icon: { [key: string]: { title: string; icon: any } } = {
  confirm: {
    title: "Are you sure you want to continue?",
    icon: "frIcon:question",
  },
  warning: {
    title: "WARNING",
    icon: "frIcon:warning-posi",
  },
  info: {
    title: "INFORMATION",
    icon: "frIcon:information",
  },
  error: {
    title: "ERROR",
    icon: "frIcon:warning-posi",
  },
  success: {
    title: "SUCCESS",
    icon: "frIcon:success",
  },
};
